import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/currency";
import Footer from "@/components/Footer";
import { FileUpload } from "@/components/FileUpload";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";
import {
  Users, Fuel, Gauge, MapPin, Truck, Building2, ShieldCheck, Loader2, Car,
  CalendarDays, ArrowRight, Tag, IdCard, Check, Star, ChevronLeft, ChevronRight,
} from "lucide-react";
import DealCountdown from "@/components/DealCountdown";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85";
const today = () => new Date().toISOString().slice(0, 10);

export default function CarDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [reviews, setReviews] = useState([]);

  const [pickup, setPickup] = useState(today());
  const [dropoff, setDropoff] = useState("");
  const [deliveryOption, setDeliveryOption] = useState("branch");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [licenseUrl, setLicenseUrl] = useState("");
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(false);
  const [fullDates, setFullDates] = useState([]);

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
    api.get(`/cars/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get(`/cars/${id}/availability`).then((r) => setFullDates(r.data.full_dates || [])).catch(() => {});
  }, [id]);

  // Live quote
  useEffect(() => {
    if (!car || !user || !pickup || !dropoff) { setQuote(null); return; }
    const payload = {
      car_id: car.id, pickup_date: pickup, dropoff_date: dropoff,
      delivery_option: deliveryOption, delivery_address: deliveryAddress,
      flight_number: flightNumber, drivers_license_url: licenseUrl || "pending",
      promo_code: promo,
    };
    const t = setTimeout(() => {
      api.post("/car-bookings/quote", payload)
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [car, user, pickup, dropoff, deliveryOption, deliveryAddress, flightNumber, licenseUrl, promo]);

  const book = async () => {
    if (!user) { navigate(`/login?return=/cars/${id}`); return; }
    if (!dropoff) { toast.error("Select a drop-off date"); return; }
    if (!licenseUrl) { toast.error("Upload your driver's license to continue"); return; }
    if (deliveryOption === "delivery" && !deliveryAddress && !flightNumber) {
      toast.error("Add a delivery address or flight number"); return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/car-bookings", {
        car_id: car.id, pickup_date: pickup, dropoff_date: dropoff,
        delivery_option: deliveryOption, delivery_address: deliveryAddress,
        flight_number: flightNumber, drivers_license_url: licenseUrl, promo_code: promo,
      });
      toast.success("Car booked! Your deposit hold is authorized.");
      navigate(`/car-booking/${data.id}/confirmation`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#060810] flex flex-col items-center justify-center text-center px-4">
      <Car className="w-12 h-12 text-slate-600 mb-4" />
      <h1 className="font-heading text-2xl text-white mb-2">Vehicle not found</h1>
      <Link to="/cars" className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm mt-4">Browse cars</Link>
    </div>
  );
  if (!car) return <div className="min-h-screen bg-[#060810] flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;

  const img = car.image_url || car.images?.[0];
  const gallery = (car.images && car.images.length ? car.images : [img]).filter(Boolean);
  const current = gallery[activeImg] || img;
  const prev = () => setActiveImg((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setActiveImg((i) => (i + 1) % gallery.length);

  return (
    <div className="min-h-screen bg-[#060810]" data-testid="car-detail-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-8 pb-16 grid lg:grid-cols-3 gap-8">
        {/* Left: details */}
        <div className="lg:col-span-2">
          <div className="relative rounded-3xl overflow-hidden aspect-[16/9] bg-[#0B0E1B]" data-testid="car-gallery">
            <img src={current ? fileUrl(current) : FALLBACK} alt={car.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
            {gallery.length > 1 && (
              <>
                <button onClick={prev} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-white hover:text-cyan-400"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={next} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-white hover:text-cyan-400"><ChevronRight className="w-5 h-5" /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {gallery.map((_, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`w-2 h-2 rounded-full transition-colors ${i === activeImg ? "bg-cyan-400" : "bg-white/40"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="gallery-thumbs">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? "border-cyan-400" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-6">
            <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border text-cyan-300 border-cyan-300/30">{car.car_type}</span>
            {car.delivery_available && <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border text-emerald-400 border-emerald-400/30 flex items-center gap-1"><Truck className="w-3 h-3" /> Delivery available</span>}
            <span className="flex items-center gap-1 text-sm" data-testid="car-detail-rating">
              <Star className="w-4 h-4 fill-[#22E67A] text-[#22E67A]" />
              {car.reviews_count > 0 ? <span className="text-white font-bold">{car.rating?.toFixed(1)} <span className="text-slate-500 font-normal">({car.reviews_count})</span></span> : <span className="text-slate-400">New</span>}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white mt-3">{car.name}</h1>
          <p className="flex items-center gap-1.5 text-slate-400 text-sm mt-2"><Building2 className="w-4 h-4 text-violet-400" /> {car.provider_name}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Spec icon={Users} label="Capacity" value={`${car.passenger_capacity} seats`} />
            <Spec icon={Gauge} label="Transmission" value={car.transmission || "Automatic"} />
            <Spec icon={Fuel} label="Fuel policy" value={car.fuel_policy} />
            <Spec icon={MapPin} label="Island" value={car.island} />
          </div>

          {car.description && (
            <div className="mt-8">
              <h2 className="font-heading text-xl font-bold text-white mb-2">About this vehicle</h2>
              <p className="text-slate-300 leading-relaxed text-sm whitespace-pre-wrap">{car.description}</p>
            </div>
          )}

          <div className="glass rounded-2xl p-5 mt-8 flex items-start gap-3">
            <Fuel className="w-5 h-5 text-fuchsia-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-white text-sm">Fuel policy: {car.fuel_policy}</p>
              <p className="text-xs text-slate-400 mt-1">Return the vehicle following this policy to avoid extra charges.</p>
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-8" data-testid="car-reviews">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-heading text-xl font-bold text-white">Reviews</h2>
              {car.reviews_count > 0 && (
                <span className="flex items-center gap-1 text-sm text-slate-300"><Star className="w-4 h-4 fill-[#22E67A] text-[#22E67A]" /> {car.rating?.toFixed(1)} · {car.reviews_count}</span>
              )}
            </div>
            {reviews.length === 0 ? (
              <p className="text-slate-500 text-sm glass rounded-2xl p-5">No reviews yet. Be the first to rate this vehicle after your trip.</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="glass rounded-2xl p-4" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white text-sm">{r.traveler_name}</span>
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#22E67A] text-[#22E67A]" : "text-slate-600"}`} />
                        ))}
                      </span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-300 mt-2 leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: booking widget */}
        <div>
          <div className="glass neon-border rounded-2xl p-6 sticky top-24" data-testid="car-booking-widget">
            <div className="flex items-end justify-between mb-5">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-caption">From</span>
                {car.deal_of_day && (
                  <div className="text-slate-500 text-sm line-through leading-none mb-0.5">{formatPrice(car.original_price_per_day, car.currency)}</div>
                )}
                <div className="font-heading font-extrabold price-red text-3xl leading-none">{formatPrice(car.price_per_day, car.currency)}</div>
              </div>
              <span className="text-slate-500 text-xs">/ day</span>
            </div>

            {car.deal_of_day && (
              <div className="flex items-center gap-2 mb-4 rounded-xl bg-gradient-to-r from-[#ff5e00]/15 to-[#d900ff]/15 border border-orange-400/25 px-3 py-2">
                <span className="text-[10px] font-mono-caption uppercase tracking-widest text-orange-300 font-bold">Deal of the Day</span>
                <DealCountdown testid="detail-countdown" className="ml-auto" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-400 mb-1.5 block">Pickup</span>
                <DatePicker testid="car-pickup-date" value={pickup} min={today()} placeholder="Pick date"
                  range={{ from: pickup, to: dropoff }} disabledDates={fullDates}
                  onChange={(d) => { setPickup(d); if (dropoff && d && dropoff < d) setDropoff(""); }} />
              </label>
              <label className="block">
                <span className="text-xs text-slate-400 mb-1.5 block">Drop-off</span>
                <DatePicker testid="car-dropoff-date" value={dropoff} min={pickup || today()} placeholder="Pick date"
                  range={{ from: pickup, to: dropoff }} disabledDates={fullDates}
                  onChange={setDropoff} />
              </label>
            </div>

            {/* Delivery option */}
            <div className="mt-4">
              <span className="text-xs text-slate-400 mb-1.5 block">Pickup method</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "branch", label: "Pick up at branch", icon: Building2 },
                  { key: "delivery", label: "Deliver to me", icon: Truck, disabled: !car.delivery_available },
                ].map((o) => (
                  <button key={o.key} type="button" disabled={o.disabled} data-testid={`car-delivery-${o.key}`}
                    onClick={() => setDeliveryOption(o.key)}
                    className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      deliveryOption === o.key ? "neon-border bg-[#0B0E1B] text-cyan-300" : "border-[#1D2640] glass text-slate-300"
                    }`}>
                    <o.icon className="w-4 h-4" /> {o.label}
                  </button>
                ))}
              </div>
              {car.delivery_available && deliveryOption === "delivery" && (
                <p className="text-[11px] text-emerald-400 mt-2">+ {formatPrice(car.delivery_fee, car.currency)} delivery fee</p>
              )}
            </div>

            {deliveryOption === "delivery" && (
              <div className="mt-3 space-y-2">
                <input data-testid="car-delivery-address" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Delivery address (hotel / villa)" className={inputCls} />
                <input data-testid="car-flight-number" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder="or Flight number (e.g. BW482)" className={inputCls} />
              </div>
            )}

            {/* Driver's license */}
            <div className="mt-4">
              <span className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5 text-orange-400" /> Driver's license</span>
              <FileUpload testid="car-license-upload" accept="image/*,application/pdf" value={licenseUrl} onUploaded={setLicenseUrl} compact />
            </div>

            {/* Promo */}
            <div className="mt-4 flex items-center gap-2 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5">
              <Tag className="w-4 h-4 text-fuchsia-400 shrink-0" />
              <input data-testid="car-promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500 uppercase" />
            </div>

            {/* Quote breakdown */}
            {user && quote && (
              <div className="mt-5 space-y-1.5 text-sm border-t border-[#1D2640] pt-4" data-testid="car-quote">
                <Row label={`${formatPrice(quote.daily_rate, car.currency)} × ${quote.days} day(s)`} value={formatPrice(quote.rental, car.currency)} />
                {quote.delivery_fee > 0 && <Row label="Delivery fee" value={formatPrice(quote.delivery_fee, car.currency)} />}
                <Row label="Platform fee (20%)" value={formatPrice(quote.platform_fee, car.currency)} muted />
                {quote.discount > 0 && <Row label="Promo discount" value={`- ${formatPrice(quote.discount, car.currency)}`} green />}
                <div className="flex justify-between items-center pt-2 border-t border-[#1D2640] mt-2">
                  <span className="text-white font-semibold">Total</span>
                  <span className="font-heading font-extrabold text-white text-xl" data-testid="car-quote-total">{formatPrice(quote.total, car.currency)}</span>
                </div>
              </div>
            )}

            {/* Deposit notice */}
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-cyan-400/5 border border-cyan-400/20 px-3 py-2.5">
              <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-slate-300 leading-relaxed">
                A refundable <span className="text-cyan-300 font-semibold">{formatPrice(car.security_deposit, car.currency)}</span> security hold is authorized on your card at booking and released after the vehicle is returned undamaged.
              </p>
            </div>

            <button onClick={book} disabled={booking} data-testid="car-book-btn"
              className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 mt-5 disabled:opacity-60">
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{user ? "Book & authorize deposit" : "Log in to book"} <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[11px] text-slate-500 text-center mt-2 flex items-center justify-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> One-tap booking · demo payment</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const inputCls = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-xl p-3">
      <Icon className="w-4 h-4 text-cyan-300 mb-1.5" />
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-white font-semibold">{value}</p>
    </div>
  );
}
function Row({ label, value, muted, green }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-400"}>{label}</span>
      <span className={green ? "text-emerald-400" : "text-slate-200"}>{value}</span>
    </div>
  );
}
